import { AxiosRequestConfig } from "axios";
import { useCallback, useRef, useState, useEffect } from "react";
import { callApi } from "src/utils/callApi";
import useUpdateEffect from "../useUpdateEffect";
import { useApiStates } from "./useApiStates";
import useIsFirstRender from "../useIsFirstRender";

type UseRequestStatus = string | number | null;

const useRequest = <D = any, R = any>(
  config: { logLevel?: string[] } = {}
): [
  results: R,
  setRequest: (request: AxiosRequestConfig<D>) => Promise<any> | void,
  abort: () => void,
  loading: boolean,
  status: UseRequestStatus
] => {
  const [result, setResults, updateMessageStatus, isMountedRef] =
    useApiStates<R>();
  const [request, setPostPutRequest] =
    useState<AxiosRequestConfig<D> | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<UseRequestStatus>("idle");
  const isFirstRender = useIsFirstRender();
  const [successCallback, setSuccessCallback] =
    useState<<T>(responseData: T) => T | any>();
  const abortController = useRef<AbortController | null>(null);

  const { logLevel = ["error"] } = config;

  const abort = useCallback(() => {
    const aborted = abortController.current?.abort(
      "Cancelled by user input. useUpdateDashboardContext"
    );
    setStatus("aborted");
    return aborted;
  }, [request]);

  const setRequest = (newRequest: AxiosRequestConfig<D>) => {
    setPostPutRequest(newRequest);
    if(logLevel.includes("request") && !isFirstRender){ 
      console.log("API Request:", newRequest);
    }
    if (newRequest?.url === undefined) return Promise.resolve();
  };

  useUpdateEffect(() => {
    if (!request) return;
    //console.log("Request changed, making API call", request);

    // create new controller
    const controller = new AbortController();
    abortController.current = controller;

    callApi({
      request,
      config: {
        isMountedRef,
        setResults,
        updateMessageStatus,
        callback: successCallback,
        abortSignal: controller.signal,
        logLevel,
        setLoadingState: setLoading,
        setStatus,
      },
    });

    // abort on request change or component unmount
    return () => {
      controller.abort("unmount or new request");
    };
  }, [request]);

  // extra safety: abort on unmount if nothing else caught it
  useEffect(() => {
    return () => {
      abortController.current?.abort("component unmounted");
    };
  }, []);

  return [result, setRequest, abort, loading, status];
};

export default useRequest;
